import { useMediaQuery } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { Theme } from '@mui/material/styles';
import React, { memo } from 'react';
import { LanguageSelector } from '~/Components';
import { BACKGROUND_PRIMARY, GRAY_BACKGROUND } from '~/Constants/Colors.constants';
import { DRAWER_WIDTH, HEADER_HEIGHT_DESKTOP_WITH_GLOBAL_ALERT } from '~/Constants/UI.constants';
import { useMockMode } from '~/Hooks';
import { Button } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import { DrawerHeader, DrawerItems, useDrawer } from '.';
import { useSelector } from 'react-redux';
import { selectCurrentWarning } from '~/store/app/warning';

const useStyles = (drawerWidth: number, hasGlobalWarning: boolean) =>
  makeStyles(
    (theme) => {
      const headerHeight = hasGlobalWarning ? HEADER_HEIGHT_DESKTOP_WITH_GLOBAL_ALERT : '0px';

      return {
        drawer: {
          [theme.breakpoints.up('md')]: {
            width: drawerWidth,
            flexShrink: 0,
          },
        },
        drawerPaper: {
          backgroundColor: BACKGROUND_PRIMARY,
          width: drawerWidth,
          position: 'fixed',
          height: '100vh',
          top: 0,
          paddingTop: 0,
          zIndex: 1200,
          [theme.breakpoints.up('lg')]: {
            height: `calc(100vh - ${headerHeight}px)`,
            top: headerHeight,
          },
        },
        content: {
          flexGrow: 1,
          padding: theme.spacing(3),
        },
        drawerContainer: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          height: '100%',
          width: '100%',
          color: GRAY_BACKGROUND,
        },
        languageDropdown: {
          marginTop: 'auto',
          padding: theme.spacing(2),
          display: 'grid',
          justifyContent: 'right',
        },
      };
    },
    { name: 'Drawer' },
  );

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Drawer = memo(({ isOpen, onClose }: DrawerProps) => {
  const hasGlobalWarning = useSelector(selectCurrentWarning);
  const { classes } = useStyles(DRAWER_WIDTH, hasGlobalWarning)();
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { menuItems } = useDrawer();

  const { isInMockMode, updateIsInMockMode } = useMockMode();

  const stopMockMode = () => {
    updateIsInMockMode(false);
  };
  const GeneralDrawerItems = (
    <>
      {isInMockMode && (
        <div className={classes.mockMode}>
          <Button onClick={stopMockMode} fullWidth size="medium" variant="outlined">
            Exit Test View
          </Button>
        </div>
      )}
      <div className={classes.languageDropdown}>
        <LanguageSelector />
      </div>
    </>
  );
  return (
    <>
      {/* Mobile View */}
      {isTablet ? (
        <MuiDrawer
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          anchor={'left'}
          classes={{
            paper: classes.drawerPaper,
          }}
          onClose={onClose}
          open={isOpen}
          variant="temporary"
        >
          <div className={classes.drawerContainer}>
            <div>
              <DrawerHeader />
              <DrawerItems menuItems={menuItems} />
            </div>
            {GeneralDrawerItems}
          </div>
        </MuiDrawer>
      ) : (
        <MuiDrawer
          classes={{
            paper: classes.drawerPaper,
          }}
          open
          variant="permanent"
        >
          <div className={classes.drawerContainer}>
            <DrawerHeader />
            <DrawerItems menuItems={menuItems} />
            {GeneralDrawerItems}
          </div>
        </MuiDrawer>
      )}
    </>
  );
});
